import axios from 'axios';
import loadImage from 'blueimp-load-image';
import thisAuth from './auth';
import router from '../router';

// create axios instance
const service = axios.create({
  timeout: 60000,
  withCredentials: true,
});

let totalR = 0;
let isRefreshing = false;
const refreshSubscribers = [];

/**
 * WAF 规避 - 判断字符串是否为有效日期格式
 * 日期字符串不需要编码（不会触发 WAF，且编码后后端解析会失败）
 */
function _isValidDate(str) {
  if (!str || typeof str !== 'string') return false;
  const datePattern = /^\d{4}[-\/]\d{1,2}[-\/]\d{1,2}([ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;
  return datePattern.test(str.trim());
}

/**
 * WAF 规避 - 对单个字符串执行双重编码（内部辅助函数）
 * 排除纯数字、日期、Data URL，其余执行 encodeURIComponent → btoa
 */
function _encodeString(str) {
  if (!str || typeof str !== 'string') return str;
  if (_isValidDate(str)) return str;
  if (!isNaN(str) && !isNaN(parseFloat(str))) return str;
  try {
    return btoa(encodeURIComponent(str));
  } catch (e) {
    return str;
  }
}

/**
 * WAF 规避 - 递归对 POST 请求数据中的字符串字段执行双重编码
 * 编码顺序：encodeURIComponent → btoa（与后端解码顺序相反）
 * 排除规则（与后端 PostParmResolveFilter 保持一致）：
 *   1. FormData（文件上传），直接跳过
 *   2. dbName 字段
 *   3. 纯数字字符串
 *   4. 日期格式字符串
 *   5. Data URL（base64 图片，如 "data:image/jpeg;base64,..."）
 *   6. null / undefined / 空字符串
 *   7. 数组内的字符串元素（同样应用上述规则）
 */
function _encodeWafData(data) {
  if (data == null) return data;
  if (data instanceof FormData) return data;
  // 数组：字符串元素直接编码，对象元素递归处理
  if (Array.isArray(data)) {
    return data.map(function (item) {
      if (item && typeof item === 'string') return _encodeString(item);
      return _encodeWafData(item);
    });
  }
  if (typeof data !== 'object') return data;

  let encoded;
  try {
    encoded = JSON.parse(JSON.stringify(data));
  } catch (e) {
    return data;
  }

  Object.keys(encoded).forEach((key) => {
    const value = encoded[key];
    if (value && typeof value === 'string') {
      // 排除 dbName 字段（系统内部标识）
      if (key.toLowerCase() === 'dbname') return;
      encoded[key] = _encodeString(value);
    } else if (value && typeof value === 'object') {
      encoded[key] = _encodeWafData(value);
    }
  });

  return encoded;
}

// axios request interceptor
service.interceptors.request.use(
  (config) => {
    const accessToken = thisAuth.getLocalAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (!!window.ActiveXObject || "ActiveXObject" in window) {
      config.url = config.url + '?date=' + new Date().getTime();
    }
    // WAF 规避：对 POST 请求的 JSON 数据执行双重编码（跳过文件上传）
    if (config.method === 'post' && config.data && !(config.data instanceof FormData)) {
      config.data = _encodeWafData(config.data);
    }
    return config;
  },
  (error) => {
    // handle request error
    return Promise.reject(error);
  },
);

// axios response interceptor
service.interceptors.response.use(
  response => response,
  (error) => {
    const errorURL = router.currentRoute.fullPath;
    if (error.message === 'Network Error') {
      router.push({
        path: '/error',
        query: {
          message: error.message,
          redirectedFrom: errorURL
        },
      });
    } else if (error.code === 'ECONNABORTED') {
      router.push({
        path: '/error',
        query: {
          message: 408,
          redirectedFrom: errorURL
        },
      });
    } else if (error.response !== undefined) {
      if (error.response.status === 408) {
        router.push({
          path: '/error',
          query: {
            message: 408,
            redirectedFrom: errorURL
          },
        });
      }
      const { config, response: { status, statusText } } = error;
      const originalRequest = config;
      const currentRoute = router.currentRoute;
      const isExternal = currentRoute.meta.external;

      // Refresh token
      if (status === 401 && statusText === 'Unauthorized') {
        if (!isRefreshing) {
          isRefreshing = true;
          const refreshToken = thisAuth.getLocalRefreshToken();

          if (refreshToken) {
            if (!isExternal) {
              thisAuth.getRefreshAccessToken(refreshToken).then((model) => {
                isRefreshing = false;
                thisAuth.setLocalAccessToken(model);
                if (totalR < 3) {
                  onRefreshed(model.accessToken);
                  totalR += 1;
                } else {
                  console.log('Please refresh page.');
                }
              }).catch((errorRefresh) => {
                console.log(errorRefresh);
                thisAuth.goLogin();
              });
            } else {
              thisAuth.getRefreshAccessTokenExternal(refreshToken).then((model) => {
                isRefreshing = false;
                thisAuth.setLocalAccessToken(model);
              }).catch((errorRefresh) => {
                console.log(errorRefresh);
                router.push({
                  path: '/external/login',
                  query: {
                    mpWorkAppId: config.MPWORKAPPID,
                    message: errorRefresh,
                    redirect_uri: currentRoute.path
                  },
                });
              });
            }
          }
        }
        const retryOrigReq = new Promise((resolve) => {
          subscribeTokenRefresh((access_token) => {
            resolve(service(originalRequest));
          });
        });
        return retryOrigReq;
      }
    }

    return Promise.reject(error);
  },
);


function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token) {
  refreshSubscribers.map(cb => cb(token));
}

function checkNested(obj) {
  const args = Array.prototype.slice.call(arguments, 1);

  for (let i = 0; i < args.length; i++) {
    if (!obj || !obj.hasOwnProperty(args[i])) {
      return false;
    }
    obj = obj[args[i]];
  }
  return true;
}
function getUrlKey(name) {
  return decodeURIComponent((new RegExp('[?|&]' + name + '=' + '([^&;]+?)(&|#|;|$)').exec(location.href) || [, ""])[1].replace(/\+/g, '%20')) || null;
}
class cokeStorage {
  setItem(key, value) {
    if (sessionStorage.getItem('isExternal') !== 'undefined') {
      return localStorage.setItem(key, value);
    } else {
      return sessionStorage.setItem(key, value);
    }
  }
  getItem(key) {
    if (sessionStorage.getItem('isExternal') !== 'undefined') {
      return localStorage.getItem(key);
    } else {
      return sessionStorage.getItem(key);
    }
  }
  removeItem(key) {
    if (sessionStorage.getItem('isExternal') !== 'undefined') {
      return localStorage.removeItem(key);
    } else {
      return sessionStorage.removeItem(key);
    }
  }
}
export default new cokeStorage();

// http://emailregex.com/
// https://github.com/VincentSit/ChinaMobilePhoneNumberRegex
const regExp = {
  email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  phone: /^1[0123456789]\d{9}$/,
};

const correctImgOrientation = (imgFile, imgHandler) => {
  loadImage(
    imgFile,
    (canvas) => {
      const dataURL = canvas.toDataURL('image/jpeg', 0.5); // this convert canvas to base64 format
      imgHandler(dataURL);
    },
    { orientation: true },
  );
};

const scrollErrMsgToView = (wrapperEleClass) => {
  const errorMsgLabel = document.querySelectorAll(`${wrapperEleClass || 'body'} .error-msg`);
  setTimeout(() => {
    let scrolled = false;
    errorMsgLabel.forEach((ele) => {
      if (!scrolled && ele.style.display !== 'none') {
        ele.previousElementSibling.scrollIntoView();
        scrolled = !scrolled;
      }
    });
  }, 300); // v-show's toggle show and hide may have a duration
};

/**
 * 对服务端返回的消息进行净化，剥除 HTML 标签及潜在的脚本注入字符，
 * 确保输出内容以纯文本形式展示，防止因渲染方式变更而引发 XSS 风险。
 */
const sanitizeServerMessage = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .replace(/<[^>]*>/g, '')       // 剥除所有 HTML 标签
    .replace(/[<>]/g, '')          // 移除残余的尖括号
    .trim();
};

export {
  service as request,
  checkNested,
  regExp,
  getUrlKey,
  correctImgOrientation,
  scrollErrMsgToView,
  sanitizeServerMessage,
};
